import { Identify, identify } from '@amplitude/analytics-browser';
import { BrowserConfig, EnrichmentPlugin, Event } from '@amplitude/analytics-types';

import { TrackBidPlacedPrebidStatus } from '@/utils/amplitude/analytics';
import { TrackingEvents } from '@/utils/amplitude/analytics/core.types';

const TOTAL_LOTS_BID_ON_USER_PROPERTY = 'Total Lots Bid On';
const TOTAL_BIDS_USER_PROPERTY = 'Total Bids';
const AUCTIONEERS_FOLLOWED_USER_PROPERTY = 'Auctioneers Followed';
const SEARCHES_FOLLOWED_USER_PROPERTY = 'Searches Followed';
const LOTS_SAVED_USER_PROPERTY = 'Lots Saved';

/**
 * <em>This should only be called from the AmplitudeProvider.</em>
 * A plugin that matches the amplitude user id to our intern
 * It also adds additional user information:
 *   1) Account Creation Date - ISO string of when the user created their account on LiveAuctioneers
 *   2) Total Bids - The number of bids the user has placed
 *   3) Total Lots Bid On - The number of items the user has placed bids on
 */
export class UserPropertiesPlugin implements EnrichmentPlugin {
    name = 'user-properties-plugin';
    type = 'enrichment' as const;

    private bidderId = 0;
    private accountCreationDate = '';
    private hasEverRegisteredForAuction = false;
    private hasEverPlacedBid = false;
    /**
     * Provide the logged-in bidder's ID and account-creation date to enrich their amplitude user profile
     * @param bidderId - The user's internal bidder ID
     * @param accountCreationDate - When was the account created?
     */
    addBidderInfo = ({
        accountCreationDate,
        bidderId,
        hasEverPlacedBid,
        hasEverRegisteredForAuction,
    }: {
        accountCreationDate: string;
        bidderId: number;
        hasEverPlacedBid: boolean;
        hasEverRegisteredForAuction: boolean;
    }) => {
        this.bidderId = bidderId;
        this.accountCreationDate = accountCreationDate;
        this.hasEverRegisteredForAuction = hasEverRegisteredForAuction;
        this.hasEverPlacedBid = hasEverPlacedBid;
    };

    private incrementUserProperty = (identifyEvent: Identify, property: string) => {
        identifyEvent.add(property, 1);
    };

    private decrementUserProperty = (identifyEvent: Identify, property: string) => {
        identifyEvent.add(property, -1);
    };

    /**
     * <em>This function should not be called directly.</em>
     *
     * This function takes the provided bidder information and adds it to the amplitude bidder profile.
     *
     * This function is automatically called when the plugin is added to amplitude.
     * This is not a constructor or akin to one. This is called explicitly through `amplitude.add`.<br/>
     * For example:
     * ```typescript
     * const plugin = new UserPropertiesPlugin(); // <-- setup not called
     * amplitude.add(plugin); // <-- setup called here!
     * ```
     */
    setup = async (_: BrowserConfig, amplitude) => {
        amplitude.setUserId(String(this.bidderId));
        const identifyEvent = new Identify();
        identifyEvent.set('Account Creation Date', this.accountCreationDate);
        identifyEvent.set('Has Ever Registered For Auction', this.hasEverRegisteredForAuction);
        identifyEvent.set('Has Ever Placed Bid', this.hasEverPlacedBid);
        amplitude.identify(identifyEvent);
    };

    /**
     * <em>This function should not be called directly.</em>
     *
     * This is called by amplitude itself when any event is triggered and the plugin is registered
     *
     * @param event - The amplitude event that was triggered. Could be an auto-track event or one of our own defined events.
     * In the case of this plugin, we check the event type and update various user properties and return the modified event.
     */
    execute = async (event: Event) => {
        let shouldUpdateUserProps = false;
        const identifyEvent = new Identify();

        if (event.event_type === TrackingEvents.Bidding.BidPlaced) {
            shouldUpdateUserProps = true;
            this.incrementUserProperty(identifyEvent, TOTAL_BIDS_USER_PROPERTY);

            const { 'User Prebid Status': userPrebidStatus } = event.event_properties;
            if (userPrebidStatus === TrackBidPlacedPrebidStatus.NoBid) {
                // only increment this value if it's an item the user has not yet bid on.
                this.incrementUserProperty(identifyEvent, TOTAL_LOTS_BID_ON_USER_PROPERTY);
            }
        }

        if (event.event_type === TrackingEvents.Bidding.BidRetracted) {
            shouldUpdateUserProps = true;
            this.decrementUserProperty(identifyEvent, TOTAL_BIDS_USER_PROPERTY);
            this.decrementUserProperty(identifyEvent, TOTAL_LOTS_BID_ON_USER_PROPERTY);
        }

        if (event.event_type === TrackingEvents.Item.ItemSaved) {
            shouldUpdateUserProps = true;
            this.incrementUserProperty(identifyEvent, LOTS_SAVED_USER_PROPERTY);
        }

        if (event.event_type === TrackingEvents.Item.ItemUnsaved) {
            shouldUpdateUserProps = true;
            this.decrementUserProperty(identifyEvent, LOTS_SAVED_USER_PROPERTY);
        }

        if (event.event_type === TrackingEvents.Auctioneer.AuctioneerFollowed) {
            shouldUpdateUserProps = true;
            this.incrementUserProperty(identifyEvent, AUCTIONEERS_FOLLOWED_USER_PROPERTY);
        }

        if (event.event_type === TrackingEvents.Auctioneer.AuctioneerUnfollowed) {
            shouldUpdateUserProps = true;
            this.decrementUserProperty(identifyEvent, AUCTIONEERS_FOLLOWED_USER_PROPERTY);
        }

        if (event.event_type === TrackingEvents.Search.SearchFollowed) {
            shouldUpdateUserProps = true;
            this.incrementUserProperty(identifyEvent, SEARCHES_FOLLOWED_USER_PROPERTY);
        }

        if (event.event_type === TrackingEvents.Search.SearchUnfollowed) {
            shouldUpdateUserProps = true;
            this.decrementUserProperty(identifyEvent, SEARCHES_FOLLOWED_USER_PROPERTY);
        }

        // Only call the $identify event if the user profile needs to be updated, otherwise, it's just a waste of an event
        if (shouldUpdateUserProps) {
            identify(identifyEvent);
        }

        return event;
    };

    /**
     * <em>This function should not be called directly</em>
     *
     * This is called by amplitude itself when the plugin is removed from its implementation.
     */
    teardown = async () => {
        this.bidderId = 0;
        this.accountCreationDate = '';
        this.hasEverRegisteredForAuction = false;
        this.hasEverPlacedBid = false;
    };
}
